/**
 * Neighbourhood Scorecard — compact 0-10 pill row.
 *
 * Displays walk / transit / food / schools / everyday scores derived
 * server-side from Overpass counts (via `/api/neighbourhood/scores`).
 * Two render modes:
 *   variant="detail"  — larger pill row for the property-detail header
 *                       + a collapsible 12-month trend chart
 *   variant="badge"   — single tiny pill for listing cards
 *
 * Colour rule: >=7 emerald · 4-6.9 amber · <4 rose.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Footprints, TrainFront, Utensils, GraduationCap, ShoppingCart,
          Loader2, TrendingUp, ChevronDown } from 'lucide-react';

const SCORES = [
  { key: 'walk',     label: 'Walk',     Icon: Footprints,    testid: 'score-walk',     colour: '#7042cc' },
  { key: 'transit',  label: 'Transit',  Icon: TrainFront,    testid: 'score-transit',  colour: '#0284c7' },
  { key: 'food',     label: 'Food',     Icon: Utensils,      testid: 'score-food',     colour: '#b45309' },
  { key: 'schools',  label: 'Schools',  Icon: GraduationCap, testid: 'score-schools',  colour: '#f59e0b' },
  { key: 'everyday', label: 'Everyday', Icon: ShoppingCart,  testid: 'score-everyday', colour: '#16a34a' },
];

const _colour = (s) => {
  if (s == null) return { bg: 'bg-gray-100', text: 'text-gray-500', ring: 'ring-gray-200' };
  if (s >= 7)   return { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' };
  if (s >= 4)   return { bg: 'bg-amber-50',   text: 'text-amber-700',   ring: 'ring-amber-200' };
  return              { bg: 'bg-rose-50',    text: 'text-rose-700',    ring: 'ring-rose-200' };
};

// Tiny module-level cache so navigating between listings that share
// the same neighbourhood grid cell doesn't refetch.
const _scoreCache = new Map();
const _historyCache = new Map();

// ── Trend chart ────────────────────────────────────────────────────────
// SVG line chart with one polyline per score. Chosen over recharts to
// avoid dragging another dependency in — the shape is simple enough
// that a hand-rolled SVG is ~40 lines and pixel-perfect.
const TrendChart = ({ history }) => {
  const { W, H, PAD, paths, labels } = useMemo(() => {
    const W = 480, H = 160, PAD = { top: 8, right: 8, bottom: 20, left: 22 };
    if (!history || history.length < 2) return { W, H, PAD, paths: [], labels: [] };
    const innerW = W - PAD.left - PAD.right;
    const innerH = H - PAD.top  - PAD.bottom;
    const xStep = innerW / (history.length - 1);
    const y = (v) => PAD.top + innerH - (v / 10) * innerH;
    const paths = SCORES.map((s) => ({
      key:    s.key,
      colour: s.colour,
      label:  s.label,
      d:      history.map((snap, i) => `${i === 0 ? 'M' : 'L'} ${PAD.left + i * xStep} ${y(snap[s.key] ?? 0)}`).join(' '),
      end:    { x: PAD.left + (history.length - 1) * xStep, y: y(history[history.length - 1][s.key] ?? 0) },
    }));
    const labels = history.map((snap, i) => ({
      x:  PAD.left + i * xStep,
      d:  snap.date,
    }));
    return { W, H, PAD, paths, labels };
  }, [history]);

  if (!history || history.length < 2) {
    return (
      <div className="text-xs text-gray-500 italic p-4 text-center"
             data-testid="scorecard-trend-empty">
        Not enough history collected yet. Check back in a few months.
      </div>
    );
  }

  return (
    <div data-testid="scorecard-trend-chart">
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} className="max-w-full h-auto">
        {/* Horizontal gridlines every 2 pts */}
        {[0, 2, 4, 6, 8, 10].map((v) => {
          const innerH = H - PAD.top - PAD.bottom;
          const y = PAD.top + innerH - (v / 10) * innerH;
          return (
            <g key={v}>
              <line x1={PAD.left} x2={W - PAD.right} y1={y} y2={y} stroke="#f3f4f6" strokeWidth="1" />
              <text x={PAD.left - 4} y={y + 3} fontSize="8" textAnchor="end" fill="#9ca3af">{v}</text>
            </g>
          );
        })}
        {/* One polyline per score */}
        {paths.map((p) => (
          <g key={p.key}>
            <path d={p.d} fill="none" stroke={p.colour} strokeWidth="1.5"
                   strokeLinecap="round" strokeLinejoin="round" />
            <circle cx={p.end.x} cy={p.end.y} r="2.5" fill={p.colour} />
          </g>
        ))}
        {/* X-axis month labels */}
        {labels.map((l, i) => (
          <text key={i} x={l.x} y={H - 4} fontSize="8" textAnchor="middle" fill="#6b7280">
            {l.d.slice(2)}
          </text>
        ))}
      </svg>
      <div className="flex flex-wrap gap-2 justify-center mt-1 text-[10px]">
        {SCORES.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full" style={{ background: s.colour }} />
            <span className="text-gray-600">{s.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export const NeighbourhoodScorecard = ({ lat, lng, variant = 'detail' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trendOpen, setTrendOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (lat == null || lng == null) return;
    const key = `${Number(lat).toFixed(3)},${Number(lng).toFixed(3)}`;
    if (_scoreCache.has(key)) { setData(_scoreCache.get(key)); return; }

    let cancelled = false;
    setLoading(true);

    // Retry once on transient failure — Overpass sometimes returns
    // 429/5xx during load spikes but a quick second attempt usually
    // succeeds.
    const fetchOnce = () => fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/neighbourhood/scores?lat=${lat}&lng=${lng}`,
    ).then((r) => r.ok ? r.json() : null).catch(() => null);

    (async () => {
      let d = await fetchOnce();
      // Backend now returns { unavailable: true } on soft failure —
      // retry that too so momentary Overpass blips heal themselves.
      if (!d || d.unavailable) {
        await new Promise((r) => setTimeout(r, 1500));
        d = await fetchOnce();
      }
      if (cancelled) return;
      if (d && !d.unavailable) _scoreCache.set(key, d);
      setData(d);
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [lat, lng]);

  // Lazy-load history the first time the buyer opens the trend panel
  // (heavier Overpass query — 5 snapshots — so no point paying for it
  // eagerly if they never click).
  const openTrend = () => {
    setTrendOpen((v) => !v);
    if (history || historyLoading || trendOpen) return;
    const key = `${Number(lat).toFixed(3)},${Number(lng).toFixed(3)}`;
    if (_historyCache.has(key)) { setHistory(_historyCache.get(key)); return; }
    setHistoryLoading(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/neighbourhood/history?lat=${lat}&lng=${lng}`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (d?.history) {
          _historyCache.set(key, d.history);
          setHistory(d.history);
        }
      })
      .catch(() => { /* silent */ })
      .finally(() => setHistoryLoading(false));
  };

  if (variant === 'badge') {
    if (!data || data.unavailable) return null;
    const scores = SCORES.map((s) => data[s.key]?.score).filter((v) => v != null);
    if (scores.length === 0) return null;
    const avg = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length * 10) / 10;
    const c = _colour(avg);
    return (
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ring-1 ${c.bg} ${c.text} ${c.ring}`}
        title={`Neighbourhood score ${avg}/10 — avg of walk, transit, food, schools, everyday`}
        data-testid="scorecard-badge"
      >
        <Footprints className="h-2.5 w-2.5" />
        {avg}
      </span>
    );
  }

  // detail variant
  return (
    <div data-testid="scorecard-detail">
      <div className="flex flex-wrap items-center gap-1.5">
        {loading && !data && (
          <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
            <Loader2 className="h-3 w-3 animate-spin" /> Scoring neighbourhood…
          </span>
        )}
        {data?.unavailable && !loading && (
          <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 italic"
                 data-testid="scorecard-unavailable">
            Scoring temporarily unavailable — try refreshing in a minute.
          </span>
        )}
        {data && !data.unavailable && SCORES.map(({ key, label, Icon, testid }) => {
          const s = data[key]?.score;
          const count = data[key]?.count;
          const radius = data[key]?.radius_m;
          const c = _colour(s);
          return (
            <span
              key={key}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold ring-1 ${c.bg} ${c.text} ${c.ring}`}
              title={`${count ?? 0} ${label.toLowerCase()} POIs within ${radius}m`}
              data-testid={testid}
            >
              <Icon className="h-3 w-3" />
              {label}
              <span className="font-mono">{s != null ? s.toFixed(1) : '—'}</span>
            </span>
          );
        })}
        {data && !data.unavailable && (
          <button
            type="button"
            onClick={openTrend}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold ring-1 bg-white text-gray-600 ring-gray-200 hover:bg-gray-50"
            data-testid="scorecard-trend-toggle"
            aria-expanded={trendOpen}
          >
            <TrendingUp className="h-3 w-3" />
            12-month trend
            <ChevronDown className={`h-3 w-3 transition-transform ${trendOpen ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>
      {trendOpen && (
        <div className="mt-3 rounded-lg ring-1 ring-gray-200 bg-white px-3 py-2"
              data-testid="scorecard-trend-panel">
          <p className="text-[11px] font-semibold text-gray-700 mb-1">Neighbourhood trend · last 12 months</p>
          {historyLoading && (
            <div className="flex items-center gap-2 text-[11px] text-gray-500 py-4 justify-center">
              <Loader2 className="h-3 w-3 animate-spin" /> Building trend from OSM history…
            </div>
          )}
          {!historyLoading && <TrendChart history={history} />}
        </div>
      )}
    </div>
  );
};

export default NeighbourhoodScorecard;
